'use client';

import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { sendGTMClickEvent } from '@/components/helpers/analytics/gtm';
import { usePathname } from 'next/navigation';
import {
  EModalVariants,
  IModalRefProps,
} from '@/components/molecules/ml-modal/ml-modal.types';
import { MlModal } from '@/components/molecules/ml-modal/ml-modal';
import { ESearchLocationFormVariants } from '@/components/molecules/ml-search-location-form/ml-search-location-form.types';
import { useLocation } from './locationContext';
import { MlSearchLocationFormFuse } from '../components/molecules';

export enum ESetLocationType {
  LOGIN = 'login',
  DEFAULT = 'default',
  PRICING = 'pricing',
  LOT_VISION = 'lotVision',
  ACCOUNT = 'account',
}

// The type for options passed to `handleOpen`
type TSetLocationModalOptions = {
  type?: ESetLocationType;
  redirect?: string;
};

// The context type
type TSetLocationModalControlContextProps = {
  handleOpen: (options?: TSetLocationModalOptions) => void;
  handleClose: () => void;
};

const SetLocationModalControlContext =
  createContext<TSetLocationModalControlContextProps>({
    handleOpen: () => {},
    handleClose: () => {},
  });

export const SetLocationModalProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const modalRef = useRef<IModalRefProps | null>(null);
  const pathname = usePathname();

  const [type, setType] = useState<ESetLocationType>(ESetLocationType.DEFAULT);
  const [redirect, setRedirect] = useState<string | null>(null);

  const handleOpen = (options?: TSetLocationModalOptions) => {
    setType(options?.type ?? ESetLocationType.DEFAULT);
    setRedirect(options?.redirect ?? null);
    modalRef?.current?.handleOpen();
    sendGTMClickEvent('Set Location', 'set-location-context', 'form-pop-up');
  };

  const handleClose = () => {
    modalRef?.current?.handleClose();
  };

  const { studioId, selectedText, onItemClick, allStudios, allLocations } =
    useLocation();

  useEffect(() => {
    handleClose();
  }, [pathname]);

  return (
    <SetLocationModalControlContext.Provider
      value={{ handleOpen, handleClose }}
    >
      <MlModal ref={modalRef} variant={EModalVariants.LOCATION}>
        <MlSearchLocationFormFuse
          studioId={studioId}
          selectedText={selectedText}
          type={type}
          redirect={redirect}
          onElementClick={onItemClick}
          title="Explore plans available where you intend to build:"
          studioData={allStudios}
          locationData={allLocations}
          variant={ESearchLocationFormVariants.MODAL}
        />
      </MlModal>
      {children}
    </SetLocationModalControlContext.Provider>
  );
};

export const useSetLocationModal = () =>
  useContext(SetLocationModalControlContext);
